/*
  Selection rules for CHECKLIST inputs (BR-5656).

  A CHECKLIST input lets the user tick several options instead of picking one, so its entry
  in the step's `values` map is an ARRAY of option values. Every other input type keeps a
  plain string. These helpers are the single place that knows the difference.
*/

export const CHECKLIST = 'CHECKLIST'

export const isChecklist = input => !!input && input.type === CHECKLIST

// Values are always read as an array so a missing / half-initialised entry behaves like
// "nothing ticked" rather than throwing.
export const selectedValues = value => (Array.isArray(value) ? value : [])

/*
  Cap on how many options may be ticked. 0 is the explicit "no cap" sentinel: the author
  types 0 to let the whole list be ticked. An absent / empty value reads the same way here,
  but it is not what an author should be saving - the admin form normalises an empty
  maxSelections on a CHECKLIST to 3, the standard for a campaign, so a converted or legacy
  input gets the standard rather than an accidental free-for-all.
*/
export const maxSelections = input => {
  const configured = Number(input && input.maxSelections) || 0
  return Math.max(0, Math.floor(configured))
}

/*
  How many options the user must tick.

  An absent / zero minSelections means "1 when the input is required, otherwise 0", which is
  the same rule the rest of the form already uses for required single-select inputs.

  The configured minimum is clamped to what is actually tickable - the cap, and the number
  of options the author configured. A minimum above either one can never be met while the
  cap holds, so the step would never let the user generate and the only way out (untick
  everything on an optional input) is not discoverable. The admin validates both author-side;
  this clamp is what makes the runtime undeadlockable whatever is already saved.
*/
export const minSelections = input => {
  const configured = Number(input && input.minSelections) || 0
  let min = Math.max(0, Math.floor(configured))

  const max = maxSelections(input)
  if (max > 0) min = Math.min(min, max)

  const available = ((input && input.options) || []).length
  if (available > 0) min = Math.min(min, available)

  return input && input.required ? Math.max(min, 1) : min
}

/*
  Puts a set of chosen values into the shape the rest of the step expects: the author's
  option order in the CMS, each value once, and nothing that is not in the option list.

  Each value appears once even when two options declare the same one, because that is what
  the API does with it - it collapses the list into a LinkedHashSet and consumes each value
  a single time. Emitting the value twice would make the cap count a single click as two.
*/
const canonicalize = (values, options = []) => {
  const chosen = new Set(values)
  const taken = new Set()
  return options
    .map(o => o && o.value)
    .filter(v => {
      if (!chosen.has(v) || taken.has(v)) return false
      taken.add(v)
      return true
    })
}

/*
  Toggles one option in or out of the selection.

  The result is always ordered by the author's option order in the CMS, not by the order the
  user clicked, so the same set of ticks always produces the same prompt. Duplicates are
  collapsed and values that are not in the option list are dropped.
*/
export const toggleSelection = (current, value, options = []) => {
  const selected = new Set(selectedValues(current))
  if (selected.has(value)) {
    selected.delete(value)
  } else {
    selected.add(value)
  }
  return canonicalize(selected, options)
}

/*
  The options flagged default in the CMS, in author order, never more than the cap allows.

  The cap is applied here because nothing downstream puts it back on: an over-cap
  preselection renders every card ticked, passes the generate gate and reaches the API,
  which keeps only the first maxSelections values. The user would see five ticks and get
  three in the image (BR-5656).
*/
export const defaultSelection = input => {
  const options = (input && input.options) || []
  const defaults = canonicalize(
    options.filter(o => o && o.default).map(o => o.value),
    options,
  )
  const max = maxSelections(input)
  return max > 0 ? defaults.slice(0, max) : defaults
}

/*
  True when no further option may be ticked. Already ticked options stay clickable so the
  user can swap one out.
*/
export const isAtCap = (input, value) => {
  const max = maxSelections(input)
  return max > 0 && selectedValues(value).length >= max
}

/*
  Whether one input is complete enough to let the user generate.

  - No CHECKLIST may be over its cap. A click cannot get there, but a preselection or a
    value carried over from an earlier config can, and the API silently drops the overflow -
    so the gate refuses rather than letting the user generate from more options than the
    image will actually use.
  - A required CHECKLIST needs at least max(minSelections, 1) ticks.
  - A non-required CHECKLIST is satisfied while untouched, but once the user has ticked
    anything it has to reach minSelections - a half-finished selection is a mistake, an
    empty one is a choice.
  - Everything else keeps the original rule: a required input needs a non-blank string.
*/
export const isInputSatisfied = (input, value) => {
  if (isChecklist(input)) {
    const count = selectedValues(value).length
    const min = minSelections(input)
    const max = maxSelections(input)
    if (max > 0 && count > max) return false
    if (input.required) return count >= Math.max(min, 1)
    return count === 0 || count >= min
  }
  if (!input || !input.required) return true
  if (value === undefined || value === null) return false
  return String(value).trim() !== ''
}

/*
  The hint under the help text telling the user how many options to pick. Returns an empty
  string when there is nothing worth saying (no cap and at most one required tick).

  `t` is the imageEnhancement translation function - this is component chrome, not author
  content, so it is translated rather than hardcoded.
*/
export const checklistHint = (input, t) => {
  const min = minSelections(input)
  const max = maxSelections(input)
  // A cap equal to the minimum is one number, not a range: the range line renders "Pick 2 to 2"
  // both for a plain "pick exactly 2" and for a contradictory saved config, once the minimum
  // has been clamped onto the cap.
  if (max > 0 && min === max) return t('checklistHintExact', { count: max })
  if (max > 0 && min > 1) return t('checklistHintRange', { min, max })
  if (max > 0) return t('checklistHintMax', { count: max })
  if (min > 1) return t('checklistHintMin', { count: min })
  return ''
}
